import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BLANK = { name: "", scope: "network", permissions: [], active: true };

export const RolesPanel = () => {
  const [roles, setRoles] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [draft, setDraft] = useState(null);

  const load = () =>
    api.get("/roles").then(({ data }) => {
      setRoles(data.roles);
      setAllPerms(data.all_permissions);
    });

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      if (draft.id) await api.put(`/roles/${draft.id}`, draft);
      else await api.post("/roles", draft);
      toast.success("Role saved");
      setDraft(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Role deleted");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggle = (perm) =>
    setDraft((d) => ({
      ...d,
      permissions: d.permissions.includes(perm)
        ? d.permissions.filter((p) => p !== perm)
        : [...d.permissions, perm],
    }));

  return (
    <div data-testid="roles-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Built-in roles are fixed. Add your own roles here and they appear in the user form.
        </p>
        <Button
          data-testid="add-role-btn"
          className="min-h-[44px]"
          onClick={() => setDraft({ ...BLANK })}
        >
          <Plus className="mr-2 h-4 w-4" /> Add role
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {roles.map((r) => (
          <Card
            key={r.key}
            className="border-brand-line shadow-none"
            data-testid={`role-card-${r.key}`}
          >
            <CardContent className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-brand-ink">{r.name}</p>
                  <p className="text-xs text-slate-500">
                    {r.scope === "network" ? "Central · all studios" : "Studio scoped"} ·{" "}
                    {r.permissions.includes("*")
                      ? "full access"
                      : `${r.permissions.length} permissions`}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {r.builtin ? (
                    <Badge className="border-0 bg-brand-tint text-brand">Built-in</Badge>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-h-[44px]"
                        data-testid={`edit-role-${r.key}`}
                        onClick={() =>
                          setDraft({
                            id: r.id,
                            name: r.name,
                            scope: r.scope,
                            permissions: r.permissions,
                            active: r.active !== false,
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="min-h-[44px]"
                        data-testid={`delete-role-${r.key}`}
                        onClick={() => remove(r)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-crimson-600" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Edit role" : "New role"}</DialogTitle>
          </DialogHeader>
          {draft && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Role name *</Label>
                  <Input
                    className="mt-1.5 min-h-[44px]"
                    data-testid="role-name-input"
                    value={draft.name}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    placeholder="Regional Sales Head"
                  />
                </div>
                <div>
                  <Label>Scope</Label>
                  <Select
                    value={draft.scope}
                    onValueChange={(v) => setDraft((d) => ({ ...d, scope: v }))}
                  >
                    <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="role-scope-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="network">Central — all studios</SelectItem>
                      <SelectItem value="studio">Studio — one studio only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Permissions</Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="role-permissions-picker">
                  {allPerms.map((perm) => {
                    const on = draft.permissions.includes(perm);
                    return (
                      <button
                        key={perm}
                        type="button"
                        data-testid={`role-perm-${perm}`}
                        onClick={() => toggle(perm)}
                        className={`min-h-[44px] rounded-full px-3 font-mono text-xs font-semibold transition-colors duration-200 ${
                          on ? "bg-brand text-white" : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {perm}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="save-role-btn"
              className="min-h-[44px]"
              onClick={save}
              disabled={!draft?.name || draft?.permissions.length === 0}
            >
              Save role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
